// #2121 v2 - Saga del flujo de derivación: listado con filtros nuevos, tipos, asignables, reassign single + bulk
import { takeLatest, takeEvery, call, put, select, cancelled } from 'redux-saga/effects';
import CONSTANTS from '../CONSTANTS';
import DeviceStudiesService from 'services/DeviceStudiesService';
import { toast } from 'react-toastify';
import {
  actionSetDeviceStudies,
  actionSetDeviceLoadingList,
  actionSetAssignableDoctors,
  actionSetLoadingAssignable,
  actionReassignSuccess,
  actionReassignError,
  actionBulkReassignOk,
  actionBulkReassignErr,
  actionGetDeviceStudies,
  actionSetDeviceTypes,
  actionSetDeviceStats,
  actionGetDeviceStats,
  actionDeviceStudyReassigned,
  actionDeviceStudyReassignedBulk,
} from 'store/actions/deviceStudiesActions';
import utilTranslations from '../../helpers/utilTranslations';

const getUser     = (s) => s.userReducer;
const getPractice = (s) => s.practicesReducer.practice;
const getDeviceDS = (s) => s.deviceStudiesReducer;

function* sagaGetDeviceStudies({ opts = {} }) {
  const source = DeviceStudiesService.getSource();
  yield put(actionSetDeviceLoadingList(true));
  try {
    const practice   = yield select(getPractice);
    const ds         = yield select(getDeviceDS);
    const practiceId = opts.practiceId || practice?._id;
    if (!practiceId) {
      yield put(actionSetDeviceLoadingList(false));
      return;
    }

    const res = yield call(DeviceStudiesService.getList, practiceId, {
      page:           opts.page           ?? ds.page,
      limit:          opts.limit          ?? ds.limit,
      datesort:       opts.datesort       ?? ds.sortDate,
      view:           opts.view           ?? ds.view,
      search:         opts.search         ?? ds.search,
      from:           opts.from           ?? ds.dateFrom,
      to:             opts.to             ?? ds.dateTo,
      deviceType:     opts.deviceType     ?? ds.deviceTypeFilter,
      assignedDoctor: opts.assignedDoctor ?? ds.assignedDoctorFilter,
      cancelToken:    source.token,
    });

    if (!res || res.status >= 400) throw new Error('device_list_error');
    yield put(actionSetDeviceStudies(res.data));
    yield put(actionGetDeviceStats());
  } catch (e) {
    if (!(yield cancelled())) {
      yield put(actionSetDeviceLoadingList(false));
    }
  } finally {
    source.cancel();
  }
}

function* sagaGetDeviceTypes() {
  try {
    const practice = yield select(getPractice);
    if (!practice?._id) return;
    const res = yield call(DeviceStudiesService.getTypes, practice._id);
    if (!res || res.status >= 400) return;
    yield put(actionSetDeviceTypes(res.data?.data || []));
  } catch (_) { /* silent */ }
}

function* sagaGetDeviceStats() {
  try {
    const practice = yield select(getPractice);
    if (!practice?._id) return;
    const res = yield call(DeviceStudiesService.getStats, practice._id);
    if (!res || res.status >= 400) return;
    yield put(actionSetDeviceStats(res.data?.data || { pending: 0, derivedToday: 0, stale: 0 }));
  } catch (_) { /* silent */ }
}

function* sagaGetAssignableDoctors({ reportId, practiceId }) {
  yield put(actionSetLoadingAssignable(true));
  try {
    const practice = yield select(getPractice);
    // En modo bulk, reportId puede ser array; tomamos el primero como referencia (todos están en la misma práctica)
    const oneId = Array.isArray(reportId) ? reportId[0] : reportId;
    const res = yield call(
      DeviceStudiesService.getAssignableDoctors,
      oneId,
      practiceId || practice?._id
    );
    if (!res || res.status >= 400) throw new Error('assignable_error');
    yield put(actionSetAssignableDoctors(res.data?.data || []));
  } catch (e) {
    yield put(actionSetAssignableDoctors([]));
  }
}

function* sagaReassignStudy({ reportId, targetDoctorId, practiceId, note }) {
  const userActive = yield select(getUser);
  const t = utilTranslations(userActive?.language || 'de', 'device-studies');
  try {
    const practice = yield select(getPractice);
    const res = yield call(
      DeviceStudiesService.reassign,
      reportId,
      targetDoctorId,
      practiceId || practice?._id,
      note || null
    );

    if (res?.status === 403) { yield put(actionReassignError('403')); toast.error(t('toast-error-403')); return; }
    if (res?.status === 409) { yield put(actionReassignError('409')); return; }
    if (res?.status === 422) { yield put(actionReassignError('422')); return; }
    if (!res || res.status >= 400) { yield put(actionReassignError('5xx')); toast.error(t('toast-error-generic')); return; }

    const ds       = yield select(getDeviceDS);
    const assigned = ds.assignableDoctors.find((d) => d.auth_user_id === targetDoctorId);
    toast.success(t('toast-assigned', { name: assigned?.firstName || '' }));

    // Optimistic update: reusamos la action del WS handler con los datos locales.
    // Si el WS broadcast del backend llega después, es idempotente.
    const study = ds.studies.find((s) => String(s._id) === String(reportId));
    const newDerivation = {
      doctor_id:   targetDoctorId,
      assigned_by: userActive?.auth_user_id || null,
      assigned_at: new Date().toISOString(),
      note:        note || null,
    };
    yield put(actionDeviceStudyReassigned({
      studyId:              reportId,
      derivations:          [...((study && study.derivations) || []), newDerivation],
      assigned_doctor_id:   targetDoctorId,
      assigned_doctor_name: assigned ? `${assigned.firstName} ${assigned.lastName}` : null,
      assigned_by:          userActive?.auth_user_id || null,
    }));

    yield put(actionReassignSuccess());
  } catch (e) {
    yield put(actionReassignError('5xx'));
    toast.error(t('toast-error-generic'));
  }
}

function* sagaBulkReassign({ reportIds, targetDoctorId, practiceId, note }) {
  const userActive = yield select(getUser);
  const t = utilTranslations(userActive?.language || 'de', 'device-studies');
  try {
    const practice = yield select(getPractice);
    const res = yield call(
      DeviceStudiesService.bulkReassign,
      reportIds,
      targetDoctorId,
      practiceId || practice?._id,
      note || null
    );

    if (res?.status === 403) { yield put(actionBulkReassignErr('403')); toast.error(t('toast-error-403')); return; }
    if (res?.status === 422) { yield put(actionBulkReassignErr('422')); return; }
    if (!res || res.status >= 400) { yield put(actionBulkReassignErr('5xx')); toast.error(t('toast-error-generic')); return; }

    const count = res.data?.modifiedCount || reportIds.length;
    const ds       = yield select(getDeviceDS);
    const assigned = ds.assignableDoctors.find((d) => d.auth_user_id === targetDoctorId);
    toast.success(t('toast-bulk-assigned', { count, name: assigned?.firstName || '' }));

    // Optimistic update: igual que en single, reusamos la action del WS handler.
    yield put(actionDeviceStudyReassignedBulk({
      studyIds:    reportIds,
      target:      targetDoctorId,
      assigned_by: userActive?.auth_user_id || null,
      assigned_at: new Date().toISOString(),
    }));

    yield put(actionBulkReassignOk(count));
  } catch (e) {
    yield put(actionBulkReassignErr('5xx'));
    toast.error(t('toast-error-generic'));
  }
}

// ---- WS sagas ----

function* sagaWsIncoming() {
  // El reducer ya hace el prepend (o incrementa pendingNewCount).
  // Aquí solo mostramos el toast informativo si el evento llegó al inbox.
  const ds = yield select(getDeviceDS);
  const userActive = yield select(getUser);
  const t = utilTranslations(userActive?.language || 'de', 'device-studies');
  if (ds.view === 'assigned') return;
  // Toast leve, auto-dismiss
  toast.info(t('toast-incoming'), { autoClose: 4000 });
}

function* sagaWsReassigned({ payload }) {
  // Si el evento incluye assigned_by que sea el usuario actual, no toast extra
  const userActive = yield select(getUser);
  const t = utilTranslations(userActive?.language || 'de', 'device-studies');
  if (payload?.assigned_by && payload.assigned_by === userActive?.auth_user_id) return;
  toast.info(t('toast-other-reassigned'), { autoClose: 4000 });
}

function* sagaRefetch() {
  yield* sagaGetDeviceStudies({ opts: {} });
}

function* sagaApplyPendingNew() {
  // Volver a página 0 + refetch
  yield put({ type: CONSTANTS.DEVICE_SET_PAGE_INDEX, val: 0 });
  yield put(actionGetDeviceStudies({ page: 0 }));
}

export function* deviceStudiesSaga() {
  yield takeLatest(CONSTANTS.DEVICE_GET_STUDIES,    sagaGetDeviceStudies);
  yield takeLatest(CONSTANTS.DEVICE_GET_TYPES,      sagaGetDeviceTypes);
  yield takeLatest(CONSTANTS.DEVICE_GET_STATS,      sagaGetDeviceStats);
  yield takeEvery( CONSTANTS.DEVICE_GET_ASSIGNABLE, sagaGetAssignableDoctors);
  yield takeLatest(CONSTANTS.DEVICE_REASSIGN,       sagaReassignStudy);
  yield takeLatest(CONSTANTS.DEVICE_BULK_REASSIGN,  sagaBulkReassign);
  // Refrescar stats en eventos WS y al asignar
  yield takeEvery([
    CONSTANTS.DEVICE_REASSIGN_SUCCESS,
    CONSTANTS.DEVICE_BULK_REASSIGN_OK,
    CONSTANTS.DEVICE_WS_INCOMING,
    CONSTANTS.DEVICE_WS_REASSIGNED,
    CONSTANTS.DEVICE_WS_REASSIGNED_BULK,
  ], sagaGetDeviceStats);
  // Refetch when filters change so the saga doesn't have to be triggered manually from each component
  yield takeLatest(
    [
      CONSTANTS.DEVICE_SET_VIEW,
      CONSTANTS.DEVICE_SET_SEARCH,
      CONSTANTS.DEVICE_SET_TYPE_FILTER,
      CONSTANTS.DEVICE_SET_ASSIGNED_FILTER,
      CONSTANTS.DEVICE_SET_DATE_RANGE,
    ],
    sagaRefetch,
  );
  // WS
  yield takeEvery(CONSTANTS.DEVICE_WS_INCOMING,   sagaWsIncoming);
  yield takeEvery(CONSTANTS.DEVICE_WS_REASSIGNED, sagaWsReassigned);
  yield takeEvery(CONSTANTS.DEVICE_WS_REASSIGNED_BULK, sagaWsReassigned);
  yield takeLatest(CONSTANTS.DEVICE_APPLY_PENDING_NEW, sagaApplyPendingNew);
}
// #2121 - End
